// Playground root. Owns the shared state — source, run parameters, the last run
// result, diagnostics, and the trace's selection / filter — and wires the
// editor to the trace: picking a trace row highlights the source line that
// produced it, via the editor's imperative handle.

import { useEffect, useMemo, useRef, useState } from "react";
import type * as MonacoNS from "monaco-editor";
import { emitCore, emitGo, planSource, run, STDLIB_FILES } from "../src/compiler.js";
import type { Diagnostic } from "../src/frontend/checker.js";
import type { RunResult } from "../src/backend/interp/interpreter.js";
import { EXAMPLES } from "./examples.js";
import { runWithReorders } from "./reorder.js";
import type { Reorder, ReorderStatus } from "./reorder.js";
import { describeKey, layoutLanes, resolveTypes } from "./trace-layout.js";
import type { Lane, Layout } from "./trace-layout.js";
import { Editor, EditorHandle, PROGRAM_FILE } from "./components/Editor.js";
import { Toolbar } from "./components/Toolbar.js";
import { DiagnosticsPanel } from "./components/DiagnosticsPanel.js";
import { DebuggerPanel } from "./components/DebuggerPanel.js";
import { ConsolePanel } from "./components/ConsolePanel.js";
import { TimelinePanel } from "./components/TimelinePanel.js";
import { LockPlanPanel } from "./components/LockPlanPanel.js";
import { Panel } from "./components/Panel.js";
import { Summary } from "./components/Summary.js";
import { TraceView } from "./components/TraceView.js";
import { ReorderStrip } from "./components/ReorderStrip.js";
import { GraphView } from "./components/GraphView.js";
import { HBGraphPanel } from "./components/HBGraphPanel.js";
import { CompiledPanel } from "./components/CompiledPanel.js";
import { useIsMobile } from "./components/useIsMobile.js";
import { MobileNav, MobileMenu } from "./components/MobileNav.js";
import type { MobileMenuItem } from "./components/MobileNav.js";

// Read-only source tabs for the standard library, so the debugger can open a
// stdlib file and point at the line a frame is executing.
const STDLIB_TABS = STDLIB_FILES.map((f) => ({ id: f.id, src: f.src }));

// The right pane's inspection tabs: the run trace, plus two godbolt-style views
// of the compiler's own output — the monomorphized Core IR and the lowered Go.
type CompiledTab = "core" | "go";

type Monaco = typeof MonacoNS;

const EXAMPLE_NAMES = Object.keys(EXAMPLES);

// --- Mobile navigation tree -------------------------------------------------
// The phone layout shows one screen at a time. The home screen's menu lists the
// views; "debugger" opens a second menu of its three panes. Titles are shared by
// the menu rows and each screen's back bar.

const TOP_MENU: readonly MobileMenuItem[] = [
  { id: "source", title: "Source", hint: "Program & standard library" },
  { id: "debugger", title: "Debugger", hint: "Locals, transaction, threads" },
  { id: "console", title: "Console", hint: "Printed output & assertions" },
  { id: "lockplan", title: "Lock plan", hint: "OCC vs. 2PL, per transaction" },
  { id: "compiler", title: "Compiler output", hint: "Core IR & lowered Go" },
  { id: "trace", title: "Trace", hint: "Every step, as lifelines" },
  { id: "graph", title: "Transaction graph", hint: "Reads & writes at this point" },
  { id: "hb", title: "Happens-before", hint: "Causal DAG & races" },
  { id: "timelines", title: "Timelines", hint: "Explore every interleaving" },
];

const DBG_MENU: readonly MobileMenuItem[] = [
  { id: "dbg-locals", title: "Locals", hint: "Bindings in the selected frame" },
  { id: "dbg-transaction", title: "Transaction", hint: "Read & write sets" },
  { id: "dbg-threads", title: "Threads", hint: "Call stacks of every thread" },
];

const SCREEN_TITLES: Record<string, string> = {
  source: "Source",
  debugger: "Debugger",
  "dbg-locals": "Locals",
  "dbg-transaction": "Transaction",
  "dbg-threads": "Threads",
  console: "Console",
  lockplan: "Lock plan",
  compiler: "Compiler output",
  trace: "Trace",
  graph: "Transaction graph",
  hb: "Happens-before",
  timelines: "Timelines",
};

/** Stable empty list, so "this example has no reorderings" doesn't change
 *  identity every render and re-trigger the effects that depend on it. */
const EMPTY_REORDERS: Reorder[] = [];

/** Two reorderings name the same drag: same step moved to the same side of the
 *  same target. Used to tell whether the run on screen is still the exact
 *  serialization the happens-before graph flipped to. */
const sameReorder = (a: Reorder, b: Reorder): boolean =>
  a.moved === b.moved && a.target === b.target && a.side === b.side;

/** The inputs of a run: everything that decides *which program* was executed,
 *  as opposed to which of its timelines was picked out. */
interface RanWith {
  src: string;
  seed: number;
  maxTime: number;
  strategy: "occ" | "hybrid";
}

/**
 * A name for the program a run was of. Everything the timeline tree caches —
 * forced runs, sampled outcomes — is keyed by a schedule prefix, and a prefix
 * means something else entirely once any of this changes, so this is the string
 * that tells the tree to forget.
 */
function ranKey(example: string, r: RanWith | null): string {
  if (!r) return `${example}|unrun`;
  return `${example}|${r.seed}|${r.maxTime}|${r.strategy}|${r.src.length}|${r.src}`;
}

// --- Graph ↔ trace selection bridge -----------------------------------------
// The trace view's "lifeline selection" (a lane index) and the graph view's node
// selection are the same choice in two representations. A txn lane's label is
// `#<id>` and a key lane's label is the address, so they map straight onto the
// graph's `txn:<id>` / `addr:<address>` node ids.

/** The graph node id a selected lifeline corresponds to, or null for a thread
 *  lane (threads aren't nodes in the graph). */
function laneNodeId(lane: Lane): string | null {
  if (lane.group === "txn") return `txn:${lane.label.replace(/^#/, "")}`;
  if (lane.group === "key") return `addr:${lane.label}`;
  return null;
}

/** The lane index for a graph node id, preferring one alive at `row` (key lanes
 *  can be reused once an object dies). -1 when no lane matches. */
function nodeIdToLaneIndex(layout: Layout, nodeId: string, row: number): number {
  const sep = nodeId.indexOf(":");
  const kind = nodeId.slice(0, sep);
  const rest = nodeId.slice(sep + 1);
  const group = kind === "txn" ? "txn" : "key";
  const label = kind === "txn" ? `#${rest}` : rest;
  let match = -1;
  layout.lanes.forEach((l, i) => {
    if (l.group !== group || l.label !== label) return;
    const alive = l.birth <= row && (l.death === -1 || l.death >= row);
    if (match === -1 || alive) match = i;
  });
  return match;
}

export function App({ monaco }: { monaco: Monaco }) {
  const [example, setExample] = useState(EXAMPLE_NAMES[0]!);

  // Source is kept per example too, so edits survive switching away and back;
  // an example defaults to its built-in source until it has been edited.
  const [sources, setSources] = useState<Record<string, string>>({});
  const source = sources[example] ?? EXAMPLES[example] ?? "";
  const setSource = (src: string): void =>
    setSources((prev) => ({ ...prev, [example]: src }));

  const [seed, setSeed] = useState(42);
  const [maxTime, setMaxTime] = useState(3);

  // What the last Run actually ran, per example.
  //
  // The timeline tree explores *that* program by running it again under forced
  // schedules, and it has to be the same one the trace came from. Reading the
  // live editor state instead meant the tree quietly re-ran half-typed source on
  // every keystroke — reporting "nothing to decide" for text that doesn't parse
  // yet, while the trace beside it still showed the last good run.
  const [ran, setRan] = useState<Record<string, RanWith>>({});
  const myRan = ran[example] ?? null;

  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);

  // Run result and error are kept per example so switching examples shows that
  // example's own trace — blank until it has been run — rather than carrying the
  // previous example's trace over.
  const [results, setResults] = useState<Record<string, RunResult | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const result = results[example] ?? null;
  const error = errors[example] ?? "";

  // Trace reorderings the user has dragged into place, kept per example like the
  // source and the result. Each is a pair of causal step ids, so it survives the
  // re-runs it causes; `runWithReorders` replays the list from a fresh run every
  // time and reports which entries it could realize.
  const [reorders, setReorders] = useState<Record<string, Reorder[]>>({});
  const [reorderState, setReorderState] = useState<
    Record<string, { statuses: ReorderStatus[]; error?: { index: number; message: string } }>
  >({});
  const myReorders = reorders[example] ?? EMPTY_REORDERS;
  const myReorderState = reorderState[example];

  // The race the happens-before graph has flipped to, if any. A flip applies the
  // race's serialization as the sole reordering; we remember which race that was
  // so the graph can draw the toggle back — but only trust it while the run on
  // screen is still *exactly* that serialization, so any other drag, branch pick,
  // or clear silently retires the flip without a handler having to notice.
  const [raceFlips, setRaceFlips] = useState<Record<string, Reorder | null>>({});
  const flippedRace =
    raceFlips[example] &&
    myReorders.length === 1 &&
    sameReorder(myReorders[0]!, raceFlips[example]!)
      ? raceFlips[example]!
      : null;

  // A node chosen in the timeline tree: scheduler picks forced from step 0, with
  // the ordinary heuristic finishing the run. Kept per example like everything
  // else that describes which execution you are looking at.
  const [branches, setBranches] = useState<Record<string, number[] | null>>({});
  const myBranch = branches[example] ?? null;

  const [selectedRow, setSelectedRow] = useState(-1);
  const [activeLane, setActiveLane] = useState(-1);
  const [lockStrategy, setLockStrategy] = useState<"occ" | "hybrid">("hybrid");
  const [compiledTab, setCompiledTab] = useState<CompiledTab>("core");
  // Which of the right column's panels are folded away. Only the console, the
  // trace and the happens-before graph open by default — the program's own
  // output, plus the two views that read the whole run at once — and the rest
  // start folded, each a panel you go looking for when you want it rather than
  // watch throughout.
  const [folded, setFolded] = useState({
    console: false,
    lockplan: true,
    trace: false,
    graph: true,
    timelines: true,
    compiled: true,
    hb: false,
  });
  const fold = (which: keyof typeof folded): void =>
    setFolded((prev) => ({ ...prev, [which]: !prev[which] }));

  // The phone layout: one screen at a time, reached by drilling down a shallow
  // tree. `mobileNav` is the path — [] is the home screen (pick example, Run,
  // then a menu of views), and each entry pushes a screen on top.
  const isMobile = useIsMobile();
  const [mobileNav, setMobileNav] = useState<string[]>([]);
  const mobileScreen = mobileNav.length > 0 ? mobileNav[mobileNav.length - 1]! : null;
  const pushScreen = (id: string): void => setMobileNav((p) => [...p, id]);
  const popScreen = (): void => setMobileNav((p) => p.slice(0, -1));

  // Whether the compiler output is on screen right now: on desktop that's its
  // panel being open, on mobile its screen being the one showing. The Core / Go
  // lowering is gated on this so it doesn't run while the view is hidden.
  const compilerVisible = isMobile ? mobileScreen === "compiler" : !folded.compiled;

  // Debugger selection: which thread's stack to show, and which of its frames'
  // locals — both derived from the selected trace row but overridable by the
  // user (click another thread / another frame). `activeFile` is the open source
  // tab, driven by the selected frame but also settable directly via the tabs.
  const [focusedThread, setFocusedThread] = useState(-1);
  const [selectedFrame, setSelectedFrame] = useState(0);
  const [activeFile, setActiveFile] = useState<string>(PROGRAM_FILE);

  // The graph quadrant can be collapsed to just its header, handing its height
  // back to the trace view above it.

  // The Core and Go views lower the *current* source (not the last run) under the
  // chosen strategy, mirroring `stm go` / a `--emit-core`. Computed only when
  // that panel is open and showing that backend, so editing with it folded away
  // doesn't re-run the compiler, and memoized so switching tabs back and forth
  // doesn't re-lower unchanged source.
  const coreOutput = useMemo(
    () => (compilerVisible && compiledTab === "core" ? emitCore(source, { strategy: lockStrategy }) : null),
    [compilerVisible, compiledTab, source, lockStrategy]
  );
  const goOutput = useMemo(
    () => (compilerVisible && compiledTab === "go" ? emitGo(source, { strategy: lockStrategy }) : null),
    [compilerVisible, compiledTab, source, lockStrategy]
  );

  const editorRef = useRef<EditorHandle>(null);

  // Lane geometry, shared by the trace view and the summary's filter chip.
  // Object types for the trace: keys gain a type (`/player/2`) and key lifelines
  // group by type into sub-bands. Shared by the layout, the view, and the chip.
  const types = useMemo(
    () => (result ? resolveTypes(result.trace.events, result.store.objectTypes()) : null),
    [result]
  );
  const layout = useMemo(
    () => (result && types ? layoutLanes(result.trace.events, types) : null),
    [result, types]
  );

  // The concurrency-control analysis, recomputed as the source (or the chosen
  // planning strategy) changes. Feeds both the panel and the inline editor
  // annotations, so it lives here rather than inside either component.
  const plan = useMemo(
    () => planSource(source, { strategy: lockStrategy }),
    [source, lockStrategy]
  );

  // The row to select by default after a run: the first trace event at or after
  // virtual time 1, so the debugger opens on a mid-run state rather than the very
  // first setup step. Falls back to the first row when the run never reaches t=1.
  const defaultRow = (res: RunResult | null): number => {
    if (!res || res.trace.events.length === 0) return -1;
    const i = res.trace.events.findIndex((e) => e.time >= 1);
    return i >= 0 ? i : 0;
  };

  /** The last trace row emitted at or before `step` — where a schedule prefix
   *  leaves the program. Walks back over steps that emitted nothing, so a
   *  prefix ending on a bare pause still lands on a row. */
  const rowAtStep = (res: RunResult | null, step: number): number => {
    const c = res?.causality;
    if (!c) return -1;
    for (let i = Math.min(step, c.steps.length - 1); i >= 0; i--) {
      const s = c.steps[i]!;
      if (s.endEvent > s.firstEvent) return s.endEvent - 1;
    }
    return -1;
  };

  /**
   * Run `src` and replay `rs` — the example's reorderings — on top of it. A
   * reordering is realized by re-running the whole program under a scheduler
   * override, so this is N+1 runs for N reorderings; see
   * docs/design/trace-reordering.md.
   *
   * `opts.keepRow` follows the selected row through a re-run by its causal event
   * id rather than its index, so dragging one event doesn't move the cursor off
   * whatever the user was looking at. `opts.quiet` marks a run the user asked
   * for from somewhere *other* than the trace — mid-drag, or by picking a node
   * in the timeline tree. It must not pull focus back to the trace or clear the
   * lane filter: they are working over there, and a re-run is not a reason to
   * take the keyboard away from them.
   */
  const runProgram = (
    name: string,
    src: string,
    s: number,
    t: number,
    strat: "occ" | "hybrid",
    rs: readonly Reorder[] = reorders[name] ?? EMPTY_REORDERS,
    opts: {
      keepRow?: boolean;
      quiet?: boolean;
      branch?: number[] | null;
      /** Select the row this scheduler step left off at, rather than keeping
       *  whatever was selected. A node of the timeline tree is a moment as much
       *  as a schedule, and everything keyed off the selected row — the trace's
       *  cursor, the debugger's threads and locals, the graph, the highlighted
       *  source line — should be showing that moment. */
      selectStep?: number;
      /** Select this trace row directly. Used to land on the exact event a race
       *  turns on — the intruding write between the victim's two reads — so the
       *  debugger opens on the moment the update is lost. */
      selectRow?: number;
    } = {}
  ): void => {
    const keepRow = opts.keepRow ?? false;
    const branch = opts.branch === undefined ? branches[name] ?? null : opts.branch;
    // Before the run, so it holds even when the run throws: what the tree may
    // explore is what was last asked for, parse error or not. Note what is *not*
    // here — the branch and the reorderings. Those pick one timeline out of the
    // program; they don't change which program it is, so choosing a node in the
    // tree leaves this identical and the tree keeps everything it has learned.
    setRan((prev) => ({ ...prev, [name]: { src, seed: s, maxTime: t, strategy: strat } }));
    const keepId = keepRow ? results[name]?.causality?.eventIds[selectedRow] : undefined;
    let res: RunResult | null = null;
    try {
      const replayed = runWithReorders(
        src,
        {
          seed: s || 42,
          maxTime: t || Infinity,
          maxSteps: 200_000,
          strategy: strat,
          debug: true,
          ...(branch ? { schedule: { from: 0, threads: branch } } : {}),
        },
        rs
      );
      res = replayed.result;
      setResults((prev) => ({ ...prev, [name]: res }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      setReorderState((prev) => ({
        ...prev,
        [name]: { statuses: replayed.statuses, error: replayed.error },
      }));
    } catch (e) {
      setResults((prev) => ({ ...prev, [name]: null }));
      setErrors((prev) => ({ ...prev, [name]: e instanceof Error ? e.message : String(e) }));
      setReorderState((prev) => ({ ...prev, [name]: { statuses: [] } }));
    }
    const rowCount = res?.trace.events.length ?? 0;
    const at =
      opts.selectRow !== undefined && opts.selectRow >= 0 && opts.selectRow < rowCount
        ? opts.selectRow
        : opts.selectStep === undefined
          ? -1
          : rowAtStep(res, opts.selectStep);
    const kept = keepId !== undefined ? res?.causality?.eventIds.indexOf(keepId) ?? -1 : -1;
    setSelectedRow(at >= 0 ? at : kept >= 0 ? kept : defaultRow(res));
    if (opts.quiet) return;
    setActiveLane(-1);
    // Request focus for the trace so the user can start stepping with the arrow
    // keys right away — on load, on example switch, and on Run. Honored by the
    // effect below once the (possibly freshly-mounted) trace element exists.
    wantTraceFocus.current = true;
  };

  // Focus the trace view when a run asked for it, as soon as it's in the DOM.
  // Runs after every render so it also covers the first run, where the trace
  // mounts only once a result exists.
  const wantTraceFocus = useRef(false);
  useEffect(() => {
    if (!wantTraceFocus.current) return;
    const el = document.getElementById("trace");
    if (el) {
      el.focus();
      wantTraceFocus.current = false;
    }
  });

  // Run on mount and whenever the strategy toggles (so the trace reflects OCC
  // vs. emulated locks immediately); otherwise only on Run.
  useEffect(() => {
    runProgram(example, source, seed, maxTime, lockStrategy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockStrategy]);

  // The debugger snapshot for the selected trace row — the state of every live
  // thread at the moment that event fired (call stacks, locals, txn read/write
  // sets). Null until a row is selected.
  const snapshot =
    result?.debug && selectedRow >= 0 ? result.debug[selectedRow] ?? null : null;

  // The graph node matching the trace's active lifeline (drives the graph's
  // selection highlight — the trace→graph half of the bridge).
  const selectedNodeId = useMemo(() => {
    if (activeLane < 0 || !layout) return null;
    const lane = layout.lanes[activeLane];
    return lane ? laneNodeId(lane) : null;
  }, [activeLane, layout]);

  // Clicking a graph node sets the trace's lifeline selection to it (toggling, as
  // clicking a lifeline does), and for a txn also focuses that thread in the
  // debugger — the graph→trace half of the bridge.
  const onSelectGraphNode = (nodeId: string): void => {
    if (!layout) return;
    const idx = nodeIdToLaneIndex(layout, nodeId, selectedRow);
    if (idx >= 0) setActiveLane((cur) => (cur === idx ? -1 : idx));
    if (nodeId.startsWith("txn:") && snapshot) {
      const id = Number(nodeId.slice(4));
      const t = snapshot.threads.find((th) => th.txn?.id === id || th.waiting?.txn === id);
      if (t) setFocusedThread(t.id);
    }
  };

  // The thread and frame currently under inspection, and the source line to
  // highlight (that frame's current position). The thread/frame default to the
  // acting thread's innermost frame but can be redirected by the user.
  const focusedThreadState = snapshot?.threads.find((t) => t.id === focusedThread) ?? snapshot?.threads[0];
  const activeFrame = focusedThreadState?.frames[selectedFrame];
  const targetFile = activeFrame ? activeFrame.source ?? PROGRAM_FILE : null;
  // Memoized so the object identity is stable while the highlighted file/line
  // don't change: the editor re-reveals (and thus scrolls to) the highlight
  // whenever this prop changes identity, so a fresh literal each render would
  // yank the editor back to the highlight line on every keystroke.
  const highlight = useMemo(
    () => (activeFrame ? { file: targetFile!, line: activeFrame.line } : null),
    [targetFile, activeFrame?.line]
  );

  // Selecting a trace row refocuses the debugger on the acting thread's
  // innermost frame (the point that produced the event).
  useEffect(() => {
    if (!snapshot) return;
    setFocusedThread(snapshot.actingThread);
    const acting = snapshot.threads.find((t) => t.id === snapshot.actingThread);
    setSelectedFrame(acting ? Math.max(0, acting.frames.length - 1) : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, result]);

  // Follow the inspected frame into its source file (a stdlib frame opens its
  // stdlib tab). User tab clicks set `activeFile` directly and stick until the
  // next frame change.
  useEffect(() => {
    if (targetFile !== null) setActiveFile(targetFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFile]);

  // Select a specific call-stack frame in a thread (from the Threads pane).
  const selectFrame = (threadId: number, index: number): void => {
    setFocusedThread(threadId);
    setSelectedFrame(index);
  };

  // --- reordering ----------------------------------------------------------
  // A drag names the two scheduler steps by their causal ids; every change
  // re-runs the whole list from a fresh run of the program.
  //
  // A drag in progress owns exactly one reordering, held here rather than in the
  // saved list and replaced outright as the pointer travels — so a gesture that
  // crosses four positions is one entry in the strip and not four, and a pointer
  // dragged back to where it started leaves nothing behind. The run on screen is
  // always `saved ++ live`, which is why the strip renders that and not `saved`.

  const [live, setLive] = useState<Reorder | null>(null);
  const liveRef = useRef<Reorder | null>(null);
  const shownReorders = live ? [...myReorders, live] : myReorders;

  const applyReorders = (next: Reorder[]): void => {
    setReorders((prev) => ({ ...prev, [example]: next }));
    runProgram(example, source, seed, maxTime, lockStrategy, next, { keepRow: true });
  };

  /** The reordering the pointer is currently describing: re-run and re-render
   *  now, but not yet part of the example's saved list. */
  const onLiveReorder = (r: Reorder | null): void => {
    liveRef.current = r;
    setLive(r);
    runProgram(example, source, seed, maxTime, lockStrategy, r ? [...myReorders, r] : myReorders, {
      keepRow: true,
      quiet: true,
    });
  };

  /** The pointer went up. What is on screen is already the run for
   *  `saved ++ live`, so keeping it is a list edit and not another run. */
  const onCommitReorder = (): void => {
    const r = liveRef.current;
    liveRef.current = null;
    setLive(null);
    if (r) setReorders((prev) => ({ ...prev, [example]: [...(prev[example] ?? []), r] }));
  };

  // --- the timeline tree ------------------------------------------------
  // Choosing a node forces the scheduler's picks up to that fork and lets the
  // heuristic finish, so the trace on screen is always a complete execution of
  // that node's timeline.
  //
  // Selecting a node clears any manual reorderings. A node and a set of drags are
  // two ways to name a schedule, and stacking them doesn't navigate: a drag pins
  // an interleaving, and once its window sits before where two sibling nodes
  // diverge, composing it onto their prefixes throws the diverging picks away and
  // both nodes replay to the *same* trace — so clicking around the tree stops
  // changing anything. The tree is the navigation; a drag is a modification of
  // wherever it lands. (Pick a node, *then* drag within it, and both still hold —
  // the drag is made against the node's own run. Only picking a different node
  // drops it.)
  const onSelectBranch = (prefix: number[] | null): void => {
    setBranches((prev) => ({ ...prev, [example]: prefix }));
    setReorders((prev) => ({ ...prev, [example]: EMPTY_REORDERS }));
    runProgram(example, source, seed, maxTime, lockStrategy, EMPTY_REORDERS, {
      keepRow: true,
      quiet: true, // the keyboard stays in the tree; they may be walking it
      branch: prefix,
      // A node is a moment: the last decision its prefix pins down. Everything
      // else in the playground follows the selected row, so this is all it takes
      // to put the trace, the threads, the locals and the graph at that moment.
      selectStep: prefix ? prefix.length - 1 : undefined,
    });
  };


  const onExampleChange = (name: string): void => {
    setExample(name);
    setActiveFile(PROGRAM_FILE);
    // Run the newly-selected example immediately (with its own, possibly edited,
    // source) so its trace and debugger show up without a manual Run click.
    const src = sources[name] ?? EXAMPLES[name] ?? "";
    runProgram(name, src, seed, maxTime, lockStrategy);
  };

  // Accepting the `txn` the timeline panel offers: drop the patched source into
  // the editor and run it, dropping any reorderings — they refer to steps of the
  // old program. The trace, the tree and its enumeration now describe the fixed
  // program, which is the point: the tree should go from "races" to "holds under
  // every ordering" in front of you.
  const applyRaceFix = (fixed: string): void => {
    setSource(fixed);
    setRaceFlips((prev) => ({ ...prev, [example]: null }));
    setReorders((prev) => ({ ...prev, [example]: EMPTY_REORDERS }));
    setBranches((prev) => ({ ...prev, [example]: null }));
    runProgram(example, fixed, seed, maxTime, lockStrategy, EMPTY_REORDERS, { branch: null });
  };

  // Flipping a race from inside the happens-before graph. The race edge says this
  // read *could* have observed that write, on a reachable schedule that changes
  // the outcome; clicking it takes that schedule. It is the race oracle's own
  // serialization applied as the sole reordering — the same drag "open the
  // serialized history" uses — so the graph redraws with the reads-from edge now
  // realized. Passing null flips back: drop the reordering, return to the default
  // order where the read misses the write. `quiet` keeps focus on the graph the
  // user is clicking in, and `keepRow` holds their trace cursor across the re-run.
  const onFlipRace = (serialization: Reorder | null): void => {
    const rs: Reorder[] = serialization ? [serialization] : EMPTY_REORDERS;
    setRaceFlips((prev) => ({ ...prev, [example]: serialization }));
    setReorders((prev) => ({ ...prev, [example]: rs }));
    setBranches((prev) => ({ ...prev, [example]: null }));
    runProgram(example, source, seed, maxTime, lockStrategy, rs, {
      branch: null,
      keepRow: true,
      quiet: true,
    });
  };

  // The active lane's label for the filter chip; key lanes get their type
  // (`/player/2`) so the chip reads the same as the key column.
  const filterLabel = (() => {
    if (activeLane < 0 || !layout) return null;
    const lane = layout.lanes[activeLane];
    if (!lane) return null;
    return lane.group === "key" ? describeKey(lane.label, types ?? undefined) : lane.label;
  })();

  // --- Shared panel bodies --------------------------------------------------
  // Each of these is one view of the run. The desktop lays them out as a column
  // of foldable panels beside the editor; the mobile tree opens them one at a
  // time. Writing each one here — rather than once per layout — keeps the two
  // from drifting.

  const editorBody = (
    <>
      <div className="editor-wrap">
        <Editor
          ref={editorRef}
          monaco={monaco}
          value={source}
          programLabel={example}
          onChange={setSource}
          onDiagnostics={setDiagnostics}
          txnPlan={plan?.entries}
          stdlibFiles={STDLIB_TABS}
          activeFile={activeFile}
          onActiveFileChange={setActiveFile}
          highlight={highlight}
        />
      </div>
      <DiagnosticsPanel
        diagnostics={diagnostics}
        onSelect={(line, col) => editorRef.current?.revealPosition(line, col)}
      />
    </>
  );

  const consoleBody = (
    <ConsolePanel result={result} selectedRow={selectedRow} onSelectRow={setSelectedRow} />
  );

  const lockPlanBody = (
    <LockPlanPanel
      plan={plan}
      strategy={lockStrategy}
      onStrategyChange={setLockStrategy}
      onSelectTxn={(line) => editorRef.current?.revealPosition(line, 1)}
    />
  );

  const compilerBody = (
    <>
      <div className="rtabs" role="tablist" aria-label="compiler output">
        {(
          [
            ["core", "Core"],
            ["go", "Go"],
          ] as const
        ).map(([id, title]) => (
          <button
            key={id}
            role="tab"
            aria-selected={compiledTab === id}
            className={compiledTab === id ? "on" : ""}
            onClick={() => setCompiledTab(id)}
          >
            {title}
          </button>
        ))}
      </div>
      {compiledTab === "core" ? (
        <CompiledPanel label="Core" result={coreOutput} />
      ) : (
        <CompiledPanel label="Go" result={goOutput} />
      )}
    </>
  );

  const traceBody = (
    <>
      {result && (
        <Summary result={result} filterLabel={filterLabel} onClearFilter={() => setActiveLane(-1)} />
      )}
      {error && <div id="error">{error}</div>}
      {result && (
        <ReorderStrip
          result={result}
          reorders={shownReorders}
          statuses={myReorderState?.statuses ?? []}
          error={myReorderState?.error}
          types={types ?? undefined}
          onRemove={(i) => applyReorders(myReorders.filter((_, j) => j !== i))}
          onClear={() => applyReorders([])}
        />
      )}
      {result && layout && (
        <TraceView
          result={result}
          layout={layout}
          types={types ?? undefined}
          selectedRow={selectedRow}
          activeLane={activeLane}
          onSelectRow={setSelectedRow}
          onToggleLane={(lane) => setActiveLane((cur) => (cur === lane ? -1 : lane))}
          onClearFilter={() => setActiveLane(-1)}
          onLiveReorder={onLiveReorder}
          onCommitReorder={onCommitReorder}
        />
      )}
    </>
  );

  const graphBody = (
    <GraphView
      snapshot={snapshot}
      store={result?.store ?? null}
      types={types ?? undefined}
      event={result && selectedRow >= 0 ? result.trace.events[selectedRow] ?? null : null}
      selectedNodeId={selectedNodeId}
      onSelectNode={onSelectGraphNode}
    />
  );

  const hbBody = (
    <HBGraphPanel
      result={result}
      source={source}
      seed={seed}
      maxTime={maxTime}
      strategy={lockStrategy}
      types={types ?? undefined}
      selectedRow={selectedRow}
      onSelectRow={setSelectedRow}
      flippedRace={flippedRace}
      onFlipRace={onFlipRace}
    />
  );

  const timelinesBody = (
    <TimelinePanel
      result={result}
      // The program the tree is shaped by — the *run's* source and parameters,
      // not the editor's, so an enumeration answers for what is on screen rather
      // than for what has been typed since.
      program={
        myRan
          ? {
              source: myRan.src,
              params: {
                seed: myRan.seed || 42,
                maxTime: myRan.maxTime || Infinity,
                strategy: myRan.strategy,
              },
            }
          : null
      }
      cacheKey={ranKey(example, myRan)}
      branch={myBranch}
      reordered={shownReorders.length > 0}
      onSelectBranch={onSelectBranch}
      onApplyFix={applyRaceFix}
      onReveal={(line) => editorRef.current?.revealPosition(line, 1)}
      types={types ?? undefined}
    />
  );

  // --- Mobile: render one screen of the drill-down tree --------------------
  const debuggerScreen = (pane: "locals" | "transaction" | "threads"): React.ReactNode => (
    <DebuggerPanel
      snapshot={snapshot}
      store={result?.store ?? null}
      focusedThread={focusedThreadState?.id ?? -1}
      selectedFrame={selectedFrame}
      onSelectFrame={selectFrame}
      panes={[pane]}
    />
  );

  const renderMobileScreen = (id: string): React.ReactNode => {
    switch (id) {
      case "source":
        return <div className="left-editor">{editorBody}</div>;
      case "debugger":
        return <MobileMenu items={DBG_MENU} onSelect={pushScreen} />;
      case "dbg-locals":
        return debuggerScreen("locals");
      case "dbg-transaction":
        return debuggerScreen("transaction");
      case "dbg-threads":
        return debuggerScreen("threads");
      case "console":
        return <div className="m-panel-body">{consoleBody}</div>;
      case "lockplan":
        return <div className="m-panel-body">{lockPlanBody}</div>;
      case "compiler":
        return <div className="m-panel-body">{compilerBody}</div>;
      case "trace":
        return <div className="m-panel-body">{traceBody}</div>;
      case "graph":
        return <div className="m-panel-body">{graphBody}</div>;
      case "hb":
        return <div className="m-panel-body">{hbBody}</div>;
      case "timelines":
        return <div className="m-panel-body">{timelinesBody}</div>;
      default:
        return null;
    }
  };

  const renderMobileHome = (): React.ReactNode => (
    <>
      <div className="m-controls">
        <label className="m-field">
          <span>example</span>
          <select value={example} onChange={(e) => onExampleChange(e.target.value)}>
            {EXAMPLE_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="m-run"
          onClick={() => runProgram(example, source, seed, maxTime, lockStrategy)}
        >
          Run ▶
        </button>
        <div className="m-params">
          <label className="m-field">
            <span>seed</span>
            <input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} />
          </label>
          <label className="m-field">
            <span>max&nbsp;time</span>
            <input
              type="number"
              step="0.05"
              value={maxTime}
              onChange={(e) => setMaxTime(Number(e.target.value))}
            />
          </label>
        </div>
      </div>
      {error && <div className="m-error">{error}</div>}
      <MobileMenu items={TOP_MENU} onSelect={pushScreen} />
    </>
  );

  if (isMobile) {
    return (
      <MobileNav
        path={mobileNav}
        homeTitle="STM Lang Playground"
        screenTitle={(id) => SCREEN_TITLES[id] ?? id}
        onBack={popScreen}
        renderHome={renderMobileHome}
        renderScreen={renderMobileScreen}
      />
    );
  }

  return (
    <>
      <Toolbar
        examples={EXAMPLE_NAMES}
        example={example}
        seed={seed}
        maxTime={maxTime}
        onExampleChange={onExampleChange}
        onSeedChange={setSeed}
        onMaxTimeChange={setMaxTime}
        onRun={() => runProgram(example, source, seed, maxTime, lockStrategy)}
      />
      <main>
        <div className="pane left">
          <div className="left-split">
            <DebuggerPanel
              snapshot={snapshot}
              store={result?.store ?? null}
              focusedThread={focusedThreadState?.id ?? -1}
              selectedFrame={selectedFrame}
              onSelectFrame={selectFrame}
            />
            <div className="left-editor">{editorBody}</div>
          </div>
        </div>
        {/* The right column: several views of the same run, each foldable, so
            the one you are working in gets the room the others give up. */}
        <div className="pane right">
          {/* The program's own output — what it printed and what it claimed —
              lifted out of the trace it is buried in. */}
          <Panel
            id="p-console"
            title="Console"
            grow={0}
            collapsed={folded.console}
            onToggle={() => fold("console")}
          >
            {consoleBody}
          </Panel>

          <Panel
            id="p-lockplan"
            title="Lock plan"
            collapsed={folded.lockplan}
            onToggle={() => fold("lockplan")}
          >
            {lockPlanBody}
          </Panel>

          <Panel
            id="p-compiled"
            title="Compiler output"
            collapsed={folded.compiled}
            onToggle={() => fold("compiled")}
          >
            {compilerBody}
          </Panel>

          <Panel
            id="p-trace"
            title="Trace"
            grow={2}
            collapsed={folded.trace}
            onToggle={() => fold("trace")}
          >
            {traceBody}
          </Panel>

          {/* The transaction-interaction graph at the selected point in time —
              the same snapshot the debugger inspects. */}
          <Panel id="p-graph" title="Transaction graph" collapsed={folded.graph} onToggle={() => fold("graph")}>
            {graphBody}
          </Panel>

          {/* The happens-before graph of the whole run: the causal DAG the
              timeline tree and race detector are built on, drawn out. */}
          <Panel id="p-hb" title="Happens-before" collapsed={folded.hb} onToggle={() => fold("hb")}>
            {hbBody}
          </Panel>

          <Panel
            id="p-timelines"
            title="Timelines"
            collapsed={folded.timelines}
            onToggle={() => fold("timelines")}
          >
            {timelinesBody}
          </Panel>

        </div>
      </main>
    </>
  );
}
